import type { ComponentProps } from 'react';

import { type ClassValue, clsxm } from '@vs-shop/utils';
import { type VariantProps, cva } from 'class-variance-authority';

/**
 * A centred status panel — one line of text, filled or quiet.
 *
 * Distinct from `Alert`, which tints its background and draws an accent bar
 * beside an icon. A filled notice is the louder, simpler form: the confirmation
 * after a form submits, or a standalone page state.
 *
 * The `quiet` tone paints nothing and fills the height it is given: a region
 * reporting its own state rather than an announcement laid over the page.
 */
const noticeVariants = cva('text-background flex justify-center rounded-lg p-4 font-medium', {
  variants: {
    tone: {
      success: 'bg-success',
      destructive: 'bg-destructive',
      warning: 'bg-warning-intense',
      info: 'bg-info',
      neutral: 'bg-background-inverted',
      quiet: 'text-foreground-muted rounded-none p-0 font-normal',
    },
  },
  defaultVariants: {
    tone: 'success',
  },
});

export type NoticeProps = Omit<ComponentProps<'div'>, 'className'> &
  VariantProps<typeof noticeVariants> & {
    className?: ClassValue;
    /** Classes for the text element. */
    classNameInner?: ClassValue;
  };

export function Notice({ tone, children, className, classNameInner, ...props }: NoticeProps) {
  return (
    <div data-slot="notice" className={clsxm(noticeVariants({ tone }), className)} {...props}>
      <p data-slot="notice-text" className={clsxm(classNameInner) || undefined}>
        {children}
      </p>
    </div>
  );
}

export { noticeVariants };
